import React, { useEffect, useRef, useState } from "react";
import DashboardShell from "@/components/DashboardShell";
import api from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader2, Plus, Home, IndianRupee, Trash2, Pencil, X, CheckCircle2, Camera, Upload as UploadIcon, MessageCircle, Phone, Inbox } from "lucide-react";
import { toast } from "sonner";

const PROPERTY_TYPES = ["Flat", "Villa", "Row House", "Office", "Shop"];
const BHK_VALUES = ["1 BHK", "2 BHK", "3 BHK", "4 BHK", "4+ BHK"];
const FURNISHING = ["Unfurnished", "Semi-Furnished", "Fully Furnished"];
const MAX_PHOTOS = 5;

const EMPTY = {
  property_type: "Flat", bhk: "2 BHK", address: "", locality: "", monthly_rent: "",
  security_deposit: "", furnishing: "Unfurnished", available_from: "",
  floor_number: "", total_floors: "", area_sqft: "", description: "", images: [],
};

// Prefix images (relative /api/... paths) with backend base for <img src>.
const resolveImg = (url) => {
  if (!url) return url;
  if (/^https?:\/\//i.test(url)) return url;
  const base = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");
  return `${base}${url}`;
};

export default function MyRentals() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [leads, setLeads] = useState([]);
  const [loadingLeads, setLoadingLeads] = useState(true);
  const [localities, setLocalities] = useState({ "Pune West": [], "Pune East": [] });
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null); // rental_id being edited
  const [form, setForm] = useState(EMPTY);
  const [submitting, setSubmitting] = useState(false);
  const [uploadingIdx, setUploadingIdx] = useState(-1); // slot currently uploading; -1 = idle
  const [uploadProgress, setUploadProgress] = useState(0);
  const fileInputRef = useRef(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/rentals/mine");
      setRows(data.rentals || []);
    } finally { setLoading(false); }
  };

  const loadLeads = async () => {
    setLoadingLeads(true);
    try {
      const { data } = await api.get("/rentals/leads/mine");
      setLeads(data.leads || []);
    } catch { setLeads([]); }
    finally { setLoadingLeads(false); }
  };

  useEffect(() => {
    load();
    loadLeads();
    api.get("/localities").then(({ data }) => setLocalities(data || {})).catch(() => {});
  }, []);

  const allLocalities = [...(localities["Pune West"] || []), ...(localities["Pune East"] || [])];
  const setF = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const openCreate = () => { setEditing(null); setForm(EMPTY); setShowForm(true); };
  const openEdit = (r) => {
    setEditing(r.rental_id);
    setForm({
      ...r,
      images: Array.from(r.images || []),
      monthly_rent: String(r.monthly_rent ?? ""),
      security_deposit: String(r.security_deposit ?? ""),
      floor_number: r.floor_number == null ? "" : String(r.floor_number),
      total_floors: r.total_floors == null ? "" : String(r.total_floors),
      area_sqft: r.area_sqft == null ? "" : String(r.area_sqft),
      description: r.description ?? "",
    });
    setShowForm(true);
  };
  const closeForm = () => { setShowForm(false); setEditing(null); setForm(EMPTY); };

  const submit = async () => {
    if (!form.address || !form.locality || !form.monthly_rent || !form.available_from) {
      toast.error("Fill in address, locality, monthly rent and availability date.");
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        ...form,
        monthly_rent: Number(form.monthly_rent),
        security_deposit: Number(form.security_deposit) || 0,
        floor_number: form.floor_number ? Number(form.floor_number) : null,
        total_floors: form.total_floors ? Number(form.total_floors) : null,
        area_sqft: form.area_sqft ? Number(form.area_sqft) : null,
        images: (form.images || []).filter(Boolean),
      };
      if (editing) {
        await api.patch(`/rentals/${editing}`, payload);
        toast.success("Rental updated.");
      } else {
        await api.post("/rentals", payload);
        toast.success("Rental listed.");
      }
      closeForm();
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save rental.");
    } finally { setSubmitting(false); }
  };

  const toggleStatus = async (r) => {
    const next = r.status === "Active" ? "Rented_Out" : "Active";
    try {
      await api.patch(`/rentals/${r.rental_id}`, { status: next });
      toast.success(`Marked ${next.replace("_", " ")}.`);
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed."); }
  };

  // ── Photo upload ──────────────────────────────────────────────────
  const onPickFiles = () => {
    if (form.images.length >= MAX_PHOTOS) {
      toast.error(`Max ${MAX_PHOTOS} photos per rental.`);
      return;
    }
    fileInputRef.current?.click();
  };

  const handleFilesSelected = async (evt) => {
    const files = Array.from(evt.target.files || []);
    evt.target.value = ""; // allow re-picking the same file
    if (!files.length) return;

    const remainingSlots = MAX_PHOTOS - form.images.length;
    if (files.length > remainingSlots) {
      toast.error(`Only ${remainingSlots} more photo${remainingSlots === 1 ? "" : "s"} allowed.`);
      files.length = remainingSlots;
    }

    for (const file of files) {
      if (!file.type.startsWith("image/")) {
        toast.error(`${file.name} is not an image.`);
        continue;
      }
      if (file.size > 6 * 1024 * 1024) {
        toast.error(`${file.name} exceeds 6 MB.`);
        continue;
      }
      const slotIdx = form.images.length; // read at start of each iteration
      setUploadingIdx(slotIdx);
      setUploadProgress(0);
      try {
        const data = new FormData();
        data.append("file", file);
        const { data: resp } = await api.post("/rentals/upload-photo", data, {
          headers: { "Content-Type": "multipart/form-data" },
          onUploadProgress: (e) => {
            if (e.total) setUploadProgress(Math.round((e.loaded * 100) / e.total));
          },
        });
        // Append the returned URL to images list (functional update to stay in sync)
        setForm((f) => ({ ...f, images: [...f.images, resp.url] }));
      } catch (err) {
        toast.error(err?.response?.data?.detail || `Upload failed: ${file.name}`);
      }
    }
    setUploadingIdx(-1);
    setUploadProgress(0);
  };

  const removeImage = (idx) => {
    setForm((f) => ({ ...f, images: f.images.filter((_, i) => i !== idx) }));
  };

  return (
    <DashboardShell>
      <div className="flex items-start justify-between gap-4 mb-6" data-testid="my-rentals">
        <div>
          <div className="overline text-sg-gold-deep">MY RENTALS</div>
          <h1 className="font-display text-4xl text-sg-navy mt-1">
            Manage your <span className="font-serif-italic text-sg-gold-deep">rental inventory.</span>
          </h1>
          <p className="text-sg-mute mt-2">{rows.length} rental listings</p>
        </div>
        {!showForm && (
          <Button onClick={openCreate} className="bg-sg-navy hover:bg-sg-navy-700 text-white" data-testid="add-rental-btn">
            <Plus className="w-4 h-4 mr-2" /> Add Rental Property
          </Button>
        )}
      </div>

      {showForm && (
        <div className="bg-white border border-sg-line rounded-3xl p-6 mb-8" data-testid="rental-form">
          <div className="flex items-center justify-between mb-5">
            <h2 className="font-display text-2xl text-sg-navy">{editing ? "Edit rental" : "Add rental property"}</h2>
            <button onClick={closeForm} className="text-sg-mute hover:text-sg-navy"><X className="w-5 h-5" /></button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div><Label>Property Type *</Label>
              <Select value={form.property_type} onValueChange={(v) => setF("property_type", v)}>
                <SelectTrigger data-testid="rf-type"><SelectValue /></SelectTrigger>
                <SelectContent>{PROPERTY_TYPES.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>BHK *</Label>
              <Select value={form.bhk} onValueChange={(v) => setF("bhk", v)}>
                <SelectTrigger data-testid="rf-bhk"><SelectValue /></SelectTrigger>
                <SelectContent>{BHK_VALUES.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="md:col-span-2"><Label>Address *</Label>
              <Input value={form.address} onChange={(e) => setF("address", e.target.value)} placeholder="Building name, street" data-testid="rf-address" />
            </div>
            <div><Label>Locality *</Label>
              <Select value={form.locality} onValueChange={(v) => setF("locality", v)}>
                <SelectTrigger data-testid="rf-locality"><SelectValue placeholder="Choose locality" /></SelectTrigger>
                <SelectContent>{allLocalities.map(l => <SelectItem key={l} value={l}>{l}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Furnishing *</Label>
              <Select value={form.furnishing} onValueChange={(v) => setF("furnishing", v)}>
                <SelectTrigger data-testid="rf-furnishing"><SelectValue /></SelectTrigger>
                <SelectContent>{FURNISHING.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Monthly Rent (₹) *</Label>
              <Input type="number" value={form.monthly_rent} onChange={(e) => setF("monthly_rent", e.target.value)} placeholder="35000" data-testid="rf-rent" />
            </div>
            <div><Label>Security Deposit (₹)</Label>
              <Input type="number" value={form.security_deposit} onChange={(e) => setF("security_deposit", e.target.value)} placeholder="70000" data-testid="rf-deposit" />
            </div>
            <div><Label>Available From *</Label>
              <Input type="date" value={form.available_from} onChange={(e) => setF("available_from", e.target.value)} data-testid="rf-available" />
            </div>
            <div><Label>Area (sq.ft.)</Label>
              <Input type="number" value={form.area_sqft} onChange={(e) => setF("area_sqft", e.target.value)} placeholder="1050" />
            </div>
            <div><Label>Floor No.</Label>
              <Input type="number" value={form.floor_number} onChange={(e) => setF("floor_number", e.target.value)} placeholder="7" />
            </div>
            <div><Label>Total Floors</Label>
              <Input type="number" value={form.total_floors} onChange={(e) => setF("total_floors", e.target.value)} placeholder="12" />
            </div>
            <div className="md:col-span-2"><Label>Description</Label>
              <Textarea rows={3} value={form.description} onChange={(e) => setF("description", e.target.value)} placeholder="Highlights, amenities, key points…" />
            </div>
            <div className="md:col-span-2">
              <div className="flex items-center justify-between mb-2">
                <Label>Photos (up to {MAX_PHOTOS})</Label>
                <span className="text-xs text-sg-mute">{form.images.length}/{MAX_PHOTOS} · JPG/PNG/WEBP · Max 6 MB each</span>
              </div>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/png,image/jpeg,image/jpg,image/webp"
                multiple
                className="hidden"
                onChange={handleFilesSelected}
                data-testid="rf-photo-input"
              />
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-3">
                {form.images.map((url, i) => (
                  <div key={i} className="relative aspect-square rounded-xl overflow-hidden border border-sg-line bg-sg-cream-soft group" data-testid={`rf-photo-thumb-${i}`}>
                    <img src={resolveImg(url)} alt={`Rental photo ${i + 1}`} className="w-full h-full object-cover" />
                    <button
                      type="button"
                      onClick={() => removeImage(i)}
                      className="absolute top-1.5 right-1.5 w-7 h-7 rounded-full bg-sg-navy/90 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                      aria-label={`Remove photo ${i + 1}`}
                      data-testid={`rf-photo-remove-${i}`}
                    >
                      <X className="w-3.5 h-3.5" />
                    </button>
                    <div className="absolute bottom-1.5 left-1.5 bg-sg-navy/80 text-white text-[10px] px-1.5 py-0.5 rounded">
                      {i + 1}
                    </div>
                  </div>
                ))}

                {uploadingIdx !== -1 && (
                  <div className="aspect-square rounded-xl overflow-hidden border border-dashed border-sg-gold bg-sg-cream-soft flex flex-col items-center justify-center gap-2 p-3" data-testid="rf-photo-uploading">
                    <Loader2 className="w-6 h-6 text-sg-gold-deep animate-spin" />
                    <div className="w-full h-1.5 bg-sg-line rounded-full overflow-hidden">
                      <div className="h-full bg-sg-gold-deep transition-all" style={{ width: `${uploadProgress}%` }} />
                    </div>
                    <div className="text-[11px] text-sg-mute">{uploadProgress}%</div>
                  </div>
                )}

                {form.images.length < MAX_PHOTOS && uploadingIdx === -1 && (
                  <button
                    type="button"
                    onClick={onPickFiles}
                    className="aspect-square rounded-xl border-2 border-dashed border-sg-line hover:border-sg-gold hover:bg-sg-cream-soft transition-colors flex flex-col items-center justify-center gap-2 text-sg-mute hover:text-sg-navy"
                    data-testid="rf-photo-add-btn"
                  >
                    <div className="w-10 h-10 rounded-full bg-white border border-sg-line flex items-center justify-center">
                      {form.images.length === 0 ? <Camera className="w-5 h-5 text-sg-gold-deep" /> : <UploadIcon className="w-5 h-5 text-sg-gold-deep" />}
                    </div>
                    <span className="text-xs font-medium">
                      {form.images.length === 0 ? "Add photos" : "Add more"}
                    </span>
                  </button>
                )}
              </div>
              {form.images.length === 0 && uploadingIdx === -1 && (
                <p className="text-[11px] text-sg-mute mt-2">
                  First photo is used as the cover on the public rental card.
                </p>
              )}
            </div>
          </div>
          <div className="mt-6 flex gap-3 justify-end">
            <Button variant="outline" onClick={closeForm}>Cancel</Button>
            <Button onClick={submit} disabled={submitting} className="bg-sg-navy hover:bg-sg-navy-700 text-white" data-testid="rf-submit">
              {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CheckCircle2 className="w-4 h-4 mr-2" />}
              {editing ? "Save changes" : "List rental"}
            </Button>
          </div>
        </div>
      )}

      <div className="bg-white border border-sg-line rounded-2xl overflow-hidden">
        {loading ? (
          <div className="p-12 text-center text-sg-mute"><Loader2 className="w-4 h-4 animate-spin inline mr-2" />Loading…</div>
        ) : rows.length === 0 ? (
          <div className="p-12 text-center text-sg-mute">
            <Home className="w-8 h-8 mx-auto mb-3 text-sg-gold-deep" />
            No rentals yet. Click <strong>Add Rental Property</strong> to list your first.
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-sg-cream-soft text-sg-mute text-xs uppercase">
              <tr>
                <th className="text-left px-4 py-3">Property</th>
                <th className="text-left px-4 py-3">Locality</th>
                <th className="text-left px-4 py-3">Rent</th>
                <th className="text-left px-4 py-3">Available</th>
                <th className="text-left px-4 py-3">Status</th>
                <th className="text-right px-4 py-3">Actions</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.rental_id} className="border-t border-sg-line" data-testid={`rental-row-${r.rental_id}`}>
                  <td className="px-4 py-3">
                    <div className="font-medium text-sg-navy">{r.bhk} · {r.property_type}</div>
                    <div className="text-xs text-sg-mute">{r.furnishing} · {r.area_sqft ? `${r.area_sqft} sqft` : "—"}</div>
                  </td>
                  <td className="px-4 py-3 text-xs">{r.locality}</td>
                  <td className="px-4 py-3 font-mono text-xs"><IndianRupee className="w-3 h-3 inline" />{Number(r.monthly_rent).toLocaleString("en-IN")}/mo</td>
                  <td className="px-4 py-3 text-xs">{r.available_from}</td>
                  <td className="px-4 py-3">
                    <span className={`px-2 py-0.5 rounded-full text-[11px] border ${r.status === "Active" ? "bg-emerald-50 text-emerald-800 border-emerald-200" : "bg-gray-50 text-gray-600 border-gray-200"}`}>
                      {r.status === "Active" ? "Active" : "Rented Out"}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-right">
                    <div className="inline-flex gap-3 text-xs">
                      <button onClick={() => openEdit(r)} className="text-sg-gold-deep inline-flex items-center gap-1" data-testid={`rental-edit-${r.rental_id}`}><Pencil className="w-3.5 h-3.5" />Edit</button>
                      <button onClick={() => toggleStatus(r)} className="text-sg-mute hover:text-sg-navy inline-flex items-center gap-1" data-testid={`rental-toggle-${r.rental_id}`}>
                        {r.status === "Active" ? <><Trash2 className="w-3.5 h-3.5" />Deactivate</> : <><CheckCircle2 className="w-3.5 h-3.5" />Reactivate</>}
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* ─────────────────────── MY RENTAL LEADS ─────────────────────── */}
      <div className="mt-10" data-testid="my-rental-leads">
        <div className="flex items-start justify-between gap-4 mb-4">
          <div>
            <div className="overline text-sg-gold-deep">MY RENTAL LEADS</div>
            <h2 className="font-display text-3xl text-sg-navy mt-1">
              Enquiries on your <span className="font-serif-italic text-sg-gold-deep">rental inventory.</span>
            </h2>
            <p className="text-sg-mute text-sm mt-1">
              {loadingLeads ? "…" : `${leads.length} enquiries`} · WhatsApp alerts sent to your registered number.
            </p>
          </div>
          <button
            onClick={loadLeads}
            className="text-xs text-sg-mute hover:text-sg-navy inline-flex items-center gap-1"
            data-testid="refresh-rental-leads-btn"
          >
            <Loader2 className={`w-3.5 h-3.5 ${loadingLeads ? "animate-spin" : ""}`} /> Refresh
          </button>
        </div>

        <div className="bg-white border border-sg-line rounded-2xl overflow-hidden">
          {loadingLeads ? (
            <div className="p-8 text-center text-sg-mute">
              <Loader2 className="w-4 h-4 animate-spin inline mr-2" /> Loading leads…
            </div>
          ) : leads.length === 0 ? (
            <div className="p-10 text-center text-sg-mute">
              <Inbox className="w-8 h-8 mx-auto mb-3 text-sg-gold-deep" />
              No rental enquiries yet. When a visitor submits the form on your rental detail page,
              they&apos;ll appear here — and you&apos;ll get a WhatsApp alert on your registered number.
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-sg-cream-soft text-sg-mute text-xs uppercase">
                <tr>
                  <th className="text-left px-4 py-3">Buyer</th>
                  <th className="text-left px-4 py-3">Property</th>
                  <th className="text-left px-4 py-3">Rent</th>
                  <th className="text-left px-4 py-3">Received</th>
                  <th className="text-left px-4 py-3">WhatsApp</th>
                  <th className="text-right px-4 py-3">Actions</th>
                </tr>
              </thead>
              <tbody>
                {leads.map((l) => {
                  const rentNum = Number(l.monthly_rent || 0);
                  const waStatus = (l.whatsapp_status || "").toLowerCase();
                  const waChip =
                    waStatus === "sent"
                      ? { klass: "bg-emerald-50 text-emerald-800 border-emerald-200", label: "Sent" }
                      : waStatus === "failed"
                      ? { klass: "bg-rose-50 text-rose-800 border-rose-200", label: `Failed${l.whatsapp_reason ? ` · ${l.whatsapp_reason}` : ""}` }
                      : waStatus === "skipped"
                      ? { klass: "bg-amber-50 text-amber-800 border-amber-200", label: "Not configured" }
                      : { klass: "bg-gray-50 text-gray-600 border-gray-200", label: "—" };
                  const digits = (l.phone || "").replace(/[^0-9]/g, "");
                  const waLink = digits ? `https://wa.me/${digits}?text=${encodeURIComponent(`Hi ${l.name || "there"}, reaching out about your enquiry for the ${l.bhk || ""} ${l.property_type || ""} in ${l.locality || "Pune"}.`)}` : null;
                  const received = l.created_at ? new Date(l.created_at).toLocaleString("en-IN", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }) : "—";
                  return (
                    <tr key={l.lead_id} className="border-t border-sg-line" data-testid={`rental-lead-row-${l.lead_id}`}>
                      <td className="px-4 py-3">
                        <div className="font-medium text-sg-navy">{l.name || "—"}</div>
                        <div className="text-xs text-sg-mute inline-flex items-center gap-1"><Phone className="w-3 h-3" />{l.phone || "—"}</div>
                      </td>
                      <td className="px-4 py-3 text-xs">
                        <div>{l.bhk || "—"} · {l.property_type || "—"}</div>
                        <div className="text-sg-mute">{l.locality || "—"}</div>
                      </td>
                      <td className="px-4 py-3 font-mono text-xs">
                        {rentNum > 0 ? <><IndianRupee className="w-3 h-3 inline" />{rentNum.toLocaleString("en-IN")}/mo</> : "—"}
                      </td>
                      <td className="px-4 py-3 text-xs text-sg-mute">{received}</td>
                      <td className="px-4 py-3">
                        <span className={`px-2 py-0.5 rounded-full text-[11px] border ${waChip.klass}`} data-testid={`rental-lead-wa-status-${l.lead_id}`}>
                          {waChip.label}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-right">
                        <div className="inline-flex gap-3 text-xs">
                          {waLink && (
                            <a href={waLink} target="_blank" rel="noreferrer" className="text-sg-gold-deep inline-flex items-center gap-1" data-testid={`rental-lead-wa-${l.lead_id}`}>
                              <MessageCircle className="w-3.5 h-3.5" />WhatsApp
                            </a>
                          )}
                          {l.rental_id && (
                            <a href={`/rental/${l.rental_id}`} target="_blank" rel="noreferrer" className="text-sg-mute hover:text-sg-navy inline-flex items-center gap-1">
                              <Home className="w-3.5 h-3.5" />View listing
                            </a>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </DashboardShell>
  );
}
